import { FC, useEffect, useMemo, useState } from 'react';
import type { AdminDocumentListItem } from '@libs/types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ListPagination } from '@components';
import { ContentLanguageProvider } from '@hooks';
import { AssessmentStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  BracketsCurlyIcon,
  ChartBarIcon,
  DownloadSimpleIcon,
  FileIcon,
  FilesIcon,
  FileTextIcon,
  XIcon,
} from '@phosphor-icons/react';

import {
  DomainResultsList,
  PriorityActionsPanel,
  ResultsStatsCards,
} from '../../assessment/components/overview';
import { mapToAssessmentOverviewViewModel } from '../../assessment/utils/assessment-mapper';
import {
  useAdminAssessmentDocuments,
  useAdminAssessmentResults,
  useDownloadAdminAssessmentDocumentsZip,
  useDownloadAdminDocument,
  useDownloadAdminReport,
} from '../api/admin-api';

import { formatBytes } from './adminFormat';
import { AdminStatusChip } from './AdminStatusChip';
import { AdminTable } from './AdminTable';
import { adminButtonSx, adminColors, CONFIDENCE_META } from './adminTheme';
import { MetadataDialog } from './MetadataDialog';

const PAGE_SIZE = 10;

type DialogView = 'documents' | 'results';

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** The assessment version whose documents/results are shown, or null when closed. */
  assessment: { id: string; title: string; version: number; status: string } | null;
}

const ConfidenceChip: FC<{ confidence: AdminDocumentListItem['confidence'] }> = observer(
  ({ confidence }) => {
    const theme = useTheme();
    const c = adminColors(theme);
    const { t } = useTrans();
    if (!confidence) return <Typography sx={{ fontSize: 12, color: c.faint }}>—</Typography>;
    const meta = CONFIDENCE_META[confidence];
    const color = meta.tone === 'green' ? c.green : meta.tone === 'warn' ? c.warn : c.error;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, backgroundColor: `${color}18`, borderRadius: 1.5, px: 1, py: 0.375, width: 'fit-content' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color }}>{t(meta.labelKey)}</Typography>
      </Box>
    );
  },
);

/** Results tab body — reuses the normal-user overview presentational components. */
const ResultsView: FC<{ tenantId: string; assessmentId: string; title: string }> = observer(
  ({ tenantId, assessmentId, title }) => {
    const theme = useTheme();
    const c = adminColors(theme);
    const { t, i18n } = useTrans();
    const { data, isLoading, isError } = useAdminAssessmentResults(tenantId, assessmentId);

    const viewModel = useMemo(() => {
      if (!data?.results) return null;
      return mapToAssessmentOverviewViewModel(data.results, title, i18n.language);
    }, [data, title, i18n.language]);

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (isError || !viewModel) {
      return (
        <Typography sx={{ fontSize: 13, color: c.faint, py: 6, textAlign: 'center' }}>
          {t('app.admin.detail.assessTab.results.unavailable')}
        </Typography>
      );
    }

    return (
      // This admin view has no per-assessment language to hand, so generated
      // content is treated as being in the viewer's own language.
      <ContentLanguageProvider language={i18n.language}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ResultsStatsCards results={viewModel.overallResults} language={viewModel.language} />
          <PriorityActionsPanel domainResults={viewModel.domainResults} />
          <DomainResultsList
            domainResults={viewModel.domainResults}
            overallResults={viewModel.overallResults}
          />
        </Box>
      </ContentLanguageProvider>
    );
  },
);

/**
 * Dialog for one assessment version, opened from the admin Assessments tab.
 * Two tabs: Documents (the attached files) and Results (the score/domain
 * breakdown, read-only, reusing the normal-user overview components). A
 * "Download report" action is available for completed assessments.
 */
export const AssessmentDocumentsDialog: FC<Props> = observer(
  ({ open, onClose, tenantId, assessment }) => {
    const theme = useTheme();
    const c = adminColors(theme);
    const { t } = useTrans();
    const [page, setPage] = useState(1);
    const [view, setView] = useState<DialogView>('documents');
    const [metaDoc, setMetaDoc] = useState<AdminDocumentListItem | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const downloadDoc = useDownloadAdminDocument();
    const downloadZip = useDownloadAdminAssessmentDocumentsZip();
    const downloadReport = useDownloadAdminReport();

    const isCompleted = assessment?.status === AssessmentStatus.COMPLETED;

    // Reset to the first page + Documents tab whenever a different assessment opens.
    useEffect(() => {
      setPage(1);
      setView('documents');
    }, [assessment?.id]);

    const handleDownloadDoc = (doc: AdminDocumentListItem) => {
      if (downloadingId) return;
      setDownloadingId(doc.id);
      downloadDoc.mutate(
        { tenantId, documentId: doc.id, fileName: doc.fileName },
        { onSettled: () => setDownloadingId(null) },
      );
    };

    const handleDownloadAll = () => {
      if (!assessment || downloadZip.isPending) return;
      downloadZip.mutate({ tenantId, assessmentId: assessment.id });
    };

    const handleDownloadReport = () => {
      if (!assessment || downloadReport.isPending) return;
      downloadReport.mutate({ tenantId, assessmentId: assessment.id });
    };

    const { data, isLoading, isFetching } = useAdminAssessmentDocuments(
      tenantId,
      open && view === 'documents' ? assessment?.id ?? null : null,
      { page, limit: PAGE_SIZE },
    );
    const rows = data?.rows ?? [];
    const meta = data?.meta;

    const columns = [
      { key: 'file', label: t('app.admin.detail.docsTab.table.file'), width: '32%' },
      { key: 'source', label: t('app.admin.detail.docsTab.table.source'), width: '18%' },
      { key: 'classification', label: t('app.admin.detail.docsTab.table.classification'), width: '16%' },
      { key: 'confidence', label: t('app.admin.detail.docsTab.table.confidence'), width: '14%' },
      { key: 'size', label: t('app.admin.detail.docsTab.table.size'), width: '8%' },
      { key: 'actions', label: t('app.admin.detail.docsTab.table.actions'), align: 'right' as const, width: '12%' },
    ];

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: 'hidden',
            width: { xs: '100%', md: '90vw' },
            maxWidth: { xs: '100%', md: 1100 },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.625,
            borderBottom: `1px solid ${c.border}`,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.5,
              backgroundColor: theme.palette.grey[200],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FilesIcon size={20} color={c.azure} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15.5, fontWeight: 600, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {assessment?.title ?? t('app.admin.detail.assessTab.documentsDialog.title')}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.125 }}>
              {t('app.admin.detail.assessTab.documentsDialog.subtitle', {
                version: assessment?.version ?? 0,
              })}
            </Typography>
          </Box>
          {isCompleted && (
            <Button
              onClick={handleDownloadReport}
              disabled={downloadReport.isPending}
              startIcon={
                downloadReport.isPending ? (
                  <CircularProgress size={15} sx={{ color: 'inherit' }} />
                ) : (
                  <FileTextIcon size={16} />
                )
              }
              sx={{ ...adminButtonSx(theme).neutral, height: 34, borderRadius: 2, px: 1.5, textTransform: 'none', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {t('app.admin.detail.assessTab.downloadReport')}
            </Button>
          )}
          {view === 'documents' && (
            <Button
              onClick={handleDownloadAll}
              disabled={downloadZip.isPending || isLoading || !rows.length}
              startIcon={
                downloadZip.isPending ? (
                  <CircularProgress size={15} sx={{ color: 'inherit' }} />
                ) : (
                  <DownloadSimpleIcon size={16} />
                )
              }
              sx={{ ...adminButtonSx(theme).neutral, height: 34, borderRadius: 2, px: 1.5, textTransform: 'none', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {t('app.admin.detail.assessTab.documentsDialog.downloadAll')}
            </Button>
          )}
          <IconButton
            onClick={onClose}
            sx={{ ...adminButtonSx(theme).neutral, width: 32, height: 32, borderRadius: 2 }}
          >
            <XIcon size={16} />
          </IconButton>
        </Box>

        <Tabs
          value={view}
          onChange={(_, v: DialogView) => setView(v)}
          sx={{
            px: 3,
            borderBottom: `1px solid ${c.border}`,
            minHeight: 0,
            '& .MuiTab-root': { textTransform: 'none', minHeight: 0, py: 1.25, fontSize: 13, color: c.sub },
            '& .Mui-selected': { color: `${c.ink} !important` },
            '& .MuiTabs-indicator': { backgroundColor: c.ink },
          }}
        >
          <Tab
            value="documents"
            iconPosition="start"
            icon={<FilesIcon size={16} />}
            label={t('app.admin.detail.assessTab.tabs.documents')}
            sx={{ gap: 0.75 }}
          />
          <Tab
            value="results"
            iconPosition="start"
            icon={<ChartBarIcon size={16} />}
            label={t('app.admin.detail.assessTab.tabs.results')}
            disabled={!isCompleted}
            sx={{ gap: 0.75 }}
          />
        </Tabs>

        <Box sx={{ px: 3, py: 2.5, maxHeight: '70vh', overflow: 'auto' }}>
          {view === 'documents' ? (
            <AdminTable
              columns={columns}
              minWidth={720}
              isLoading={isLoading}
              isEmpty={!rows.length}
              emptyLabel={t('app.admin.detail.assessTab.documentsDialog.empty')}
              footer={
                meta && meta.totalCount > 0 ? (
                  <ListPagination
                    page={meta.currentPage}
                    totalPages={meta.totalPages}
                    onPageChange={setPage}
                    isLoading={isFetching}
                    sx={{ mt: 0 }}
                  />
                ) : undefined
              }
            >
              {rows.map((d) => (
                <TableRow key={d.id}>
                  <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125, minWidth: 0 }}>
                      <FileIcon size={17} color={d.classificationStatus === 'failed' ? c.error : c.sub} style={{ flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {d.fileName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12, color: c.sub }}>{d.source}</TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
                    {d.classificationStatus ? (
                      <AdminStatusChip status={d.classificationStatus} />
                    ) : (
                      <Typography sx={{ fontSize: 12, color: c.faint }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
                    <ConfidenceChip confidence={d.confidence} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12, color: c.sub }}>{formatBytes(d.fileSize)}</TableCell>
                  <TableCell align="right" sx={{ borderBottom: `1px solid ${c.border}` }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                      <IconButton
                        onClick={() => handleDownloadDoc(d)}
                        disabled={downloadingId !== null}
                        title={t('app.admin.detail.docsTab.download')}
                        sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: theme.palette.grey[200] }}
                      >
                        {downloadingId === d.id ? (
                          <CircularProgress size={14} sx={{ color: c.sub }} />
                        ) : (
                          <DownloadSimpleIcon size={15} color={c.sub} />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => setMetaDoc(d)}
                        title={t('app.admin.detail.docsTab.inspect')}
                        sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: theme.palette.grey[200] }}
                      >
                        <BracketsCurlyIcon size={15} color={c.violet} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </AdminTable>
          ) : (
            assessment && isCompleted && (
              <ResultsView tenantId={tenantId} assessmentId={assessment.id} title={assessment.title} />
            )
          )}
        </Box>

        <MetadataDialog open={!!metaDoc} onClose={() => setMetaDoc(null)} doc={metaDoc} />
      </Dialog>
    );
  },
);
